import { Injectable, Signal } from '@angular/core';
import { injectQuery, QueryKey } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { BaseApiService } from './base.api.service';

@Injectable({ providedIn: 'root' })
export class SiteService extends BaseApiService {
  private listSitesKey: QueryKey = ['sites'];
  private intervenantsForSiteKey: QueryKey = ['intervenantsForSite'];
  private prestationsForSiteKey: QueryKey = ['prestationsForSite'];
  private disponibilitesKey: QueryKey = ['disponibilites'];

  getSitesAvailables() {
    return injectQuery(() => ({
      queryKey: this.listSitesKey,
      queryFn: async () => {
        try {
          return await lastValueFrom(
            this.httpClient.get<any[]>(
              `${this.baseUrl}/api/meetings/api/sitesAvailables`,
              {
                withCredentials: true,
              }
            )
          );
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
    }));
  }

  getSiteIntervenantsAvailables(siteId: Signal<string>) {
    return injectQuery(() => ({
      queryKey: [...this.intervenantsForSiteKey, siteId()],
      enabled: !!siteId(),
      queryFn: async () => {
        try {
          if (!siteId?.()) return [];
          return await lastValueFrom(
            this.httpClient.get<any[]>(
              `${this.baseUrl}/api/meetings/api/intervenantsAvailables?siteId=` +
                siteId(),
              {
                withCredentials: true,
              }
            )
          );
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
    }));
  }

  getTypesRDVAvailables(siteId: Signal<string>, intervenantId: Signal<string>) {
    return injectQuery(() => ({
      queryKey: [...this.prestationsForSiteKey, siteId(), intervenantId()],
      enabled: !!siteId() && !!intervenantId(),
      queryFn: async () => {
        try {
          if (!siteId?.()) return [];
          let url =
            `${this.baseUrl}/api/meetings/api/typesRDVAvailables?siteId=` +
            siteId();
          if (intervenantId?.()) {
            url += `&intervenantId=${intervenantId()}`;
          }
          return await lastValueFrom(
            this.httpClient.get<any[]>(url, {
              withCredentials: true,
            })
          );
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
    }));
  }

  getDisponibilitesAvailables(
    siteId: Signal<string>,
    intervenantId: Signal<string>,
    typeRDVId: Signal<string>,
    dateDebut: Signal<string>,
    dateFin: Signal<string>
  ) {
    return injectQuery(() => ({
      queryKey: [
        ...this.disponibilitesKey,
        siteId(),
        intervenantId(),
        typeRDVId(),
        dateDebut(),
        dateFin(),
      ],
      enabled: !!siteId() && !!intervenantId() && !!typeRDVId(),
      queryFn: async () => {
        try {
          if (!siteId?.()) return [];
          let url = `${
            this.baseUrl
          }/api/meetings/api/disponibilitesAvailables?siteId=${siteId()}&typeRDVId=${typeRDVId()}&dateDebut=${dateDebut()}&dateFin=${dateFin()}`;
          if (intervenantId?.()) {
            url += `&intervenantId=${intervenantId()}`;
          }

          return await lastValueFrom(
            this.httpClient.get<any[]>(url, {
              withCredentials: true,
            })
          );
        } catch (error: any) {
          this.handleError(error);
          throw error;
        }
      },
      select: (disponibilites) =>
        disponibilites.map((dispo) => {
          const formattedDate = new Date(dispo.dateJour).toLocaleDateString(
            'fr-FR',
            {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            }
          );

          // Mettre la première lettre en majuscule
          const formattedDateCapitalized =
            formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

          return {
            ...dispo,
            jourFormatted: formattedDateCapitalized,
          };
        }),
    }));
  }
}
